import { Container, Graphics, Text, TextStyle, Sprite, Texture, Rectangle } from 'pixi.js'
import type { SlotSymbol } from '@/types/slot'

export interface ReelLayout {
  reelCount: number
  rowCount: number
  symbolWidth: number
  symbolHeight: number
  reelGap: number
}

const SYMBOL_TYPE_COLORS: Record<string, number> = {
  wild: 0x00e701,
  scatter: 0x1475e1,
  bonus: 0x9b59b6,
  regular: 0x213743,
}

/**
 * ReelContainer manages the PixiJS display for slot reels.
 * Creates N reel columns with masked visible areas and symbol rendering.
 */
export class ReelContainer {
  readonly container: Container
  private reels: Container[] = []
  private reelMasks: Graphics[] = []
  private symbolContainers: Container[][] = []
  private layout: ReelLayout
  private symbols: SlotSymbol[]
  private symbolMap: Map<string, SlotSymbol>
  private spriteTextures: Map<number, Texture> = new Map()

  constructor(layout: ReelLayout, symbols: SlotSymbol[]) {
    this.layout = { ...layout }
    this.symbols = symbols
    this.symbolMap = new Map(symbols.map((s) => [s.id, s]))
    this.container = new Container()
    this.container.name = 'reel-container'
    this.buildReels()
  }

  private buildReels(): void {
    const { reelCount, rowCount, symbolWidth, symbolHeight, reelGap } = this.layout

    for (let r = 0; r < reelCount; r++) {
      const reel = new Container()
      reel.name = `reel-${r}`
      reel.x = r * (symbolWidth + reelGap)

      // Mask clips content to visible reel area only
      const mask = new Graphics()
      mask.beginFill(0xffffff)
      mask.drawRect(reel.x, 0, symbolWidth, symbolHeight * rowCount)
      mask.endFill()
      reel.mask = mask
      this.reelMasks.push(mask)
      this.container.addChild(mask)

      // Create placeholder symbol containers for each visible row
      const rowContainers: Container[] = []
      for (let row = 0; row < rowCount; row++) {
        const sym = this.createPlaceholder('')
        sym.y = row * symbolHeight
        rowContainers.push(sym)
        reel.addChild(sym)
      }

      this.symbolContainers.push(rowContainers)
      this.reels.push(reel)
      this.container.addChild(reel)
    }
  }

  /** Create fallback symbol using colored rectangle + text label */
  private createPlaceholder(symbolId: string): Container {
    const { symbolWidth, symbolHeight } = this.layout
    const c = new Container()
    c.name = `symbol-${symbolId}`

    const def = this.symbolMap.get(symbolId)
    const color = SYMBOL_TYPE_COLORS[def?.type ?? 'regular'] ?? SYMBOL_TYPE_COLORS.regular

    const bg = new Graphics()
    bg.beginFill(color, 0.8)
    bg.lineStyle(1, 0xffffff, 0.15)
    bg.drawRoundedRect(2, 2, symbolWidth - 4, symbolHeight - 4, 6)
    bg.endFill()
    c.addChild(bg)

    if (symbolId && def) {
      const text = new Text(
        def.name.charAt(0).toUpperCase(),
        new TextStyle({
          fontFamily: 'DM Sans, sans-serif',
          fontSize: Math.min(symbolWidth, symbolHeight) * 0.35,
          fill: 0xffffff,
          fontWeight: 'bold',
        }),
      )
      text.anchor.set(0.5)
      text.x = symbolWidth / 2
      text.y = symbolHeight / 2
      c.addChild(text)
    }

    return c
  }

  /** Create a symbol display — uses sprite texture if available, else placeholder */
  private createSymbol(symbolId: string): Container {
    const def = this.symbolMap.get(symbolId)
    if (!def) return this.createPlaceholder(symbolId)

    const texture = this.spriteTextures.get(def.sprite_index)
    if (texture && texture !== Texture.EMPTY) {
      const { symbolWidth, symbolHeight } = this.layout
      const c = new Container()
      c.name = `symbol-${symbolId}`

      const sprite = new Sprite(texture)
      sprite.width = symbolWidth - 4
      sprite.height = symbolHeight - 4
      sprite.x = 2
      sprite.y = 2
      c.addChild(sprite)

      return c
    }

    return this.createPlaceholder(symbolId)
  }

  /** Load a sprite sheet and extract sub-textures for each symbol index */
  loadSpriteSheet(texture: Texture, symbolCount: number): void {
    if (!texture || texture === Texture.EMPTY || symbolCount <= 0) return

    const frameWidth = texture.width / symbolCount
    const frameHeight = texture.height

    for (let i = 0; i < symbolCount; i++) {
      const frame = new Rectangle(i * frameWidth, 0, frameWidth, frameHeight)
      this.spriteTextures.set(i, new Texture(texture.baseTexture, frame))
    }
  }

  /** Update all visible symbols from a grid. grid[reel][row] = symbolId */
  setSymbols(grid: string[][]): void {
    const { reelCount, rowCount } = this.layout

    for (let r = 0; r < Math.min(grid.length, reelCount); r++) {
      const reelSymbols = grid[r]
      if (!reelSymbols) continue

      for (let row = 0; row < Math.min(reelSymbols.length, rowCount); row++) {
        this.updateSymbolAt(r, row, reelSymbols[row])
      }
    }
  }

  private updateSymbolAt(reel: number, row: number, symbolId: string): void {
    const reelObj = this.reels[reel]
    if (!reelObj) return

    const old = this.symbolContainers[reel]?.[row]
    if (old) {
      reelObj.removeChild(old)
      old.destroy({ children: true })
    }

    const sym = this.createSymbol(symbolId)
    sym.y = row * this.layout.symbolHeight
    this.symbolContainers[reel][row] = sym
    reelObj.addChild(sym)
  }

  /** Total width of all reels including gaps */
  get totalWidth(): number {
    const { reelCount, symbolWidth, reelGap } = this.layout
    return reelCount * symbolWidth + Math.max(0, reelCount - 1) * reelGap
  }

  /** Total height of visible reel area */
  get totalHeight(): number {
    return this.layout.rowCount * this.layout.symbolHeight
  }

  /** Get individual reel container */
  getReel(index: number): Container | undefined {
    return this.reels[index]
  }

  /** Number of reels */
  get reelCount(): number {
    return this.reels.length
  }

  /** Get a copy of the layout config */
  getLayout(): ReelLayout {
    return { ...this.layout }
  }

  /** Get the symbol container at a specific position */
  getSymbolAt(reel: number, row: number): Container | undefined {
    return this.symbolContainers[reel]?.[row]
  }

  /** Get the mask for a specific reel */
  getMask(index: number): Graphics | undefined {
    return this.reelMasks[index]
  }

  /** Resize the entire reel display to fit within given dimensions */
  resize(targetWidth: number, targetHeight: number): void {
    const scaleX = targetWidth / this.totalWidth
    const scaleY = targetHeight / this.totalHeight
    const scale = Math.min(scaleX, scaleY)

    this.container.scale.set(scale)
    this.container.x = (targetWidth - this.totalWidth * scale) / 2
    this.container.y = (targetHeight - this.totalHeight * scale) / 2
  }

  /**
   * Extend a reel with extra symbols below the visible area for smooth scroll animation.
   */
  extendReelStrip(reelIndex: number, symbolIds: string[]): void {
    const reel = this.reels[reelIndex]
    if (!reel) return

    const startY = this.layout.rowCount * this.layout.symbolHeight
    for (let i = 0; i < symbolIds.length; i++) {
      const sym = this.createSymbol(symbolIds[i])
      sym.y = startY + i * this.layout.symbolHeight
      sym.name = `spin-extra-${i}`
      reel.addChild(sym)
    }
  }

  /**
   * Remove all extra spin symbols from a reel, keeping only the original visible rows.
   */
  clearSpinStrip(reelIndex: number): void {
    const reel = this.reels[reelIndex]
    if (!reel) return

    const toRemove: Container[] = []
    for (const child of reel.children) {
      if (child.name?.startsWith('spin-extra-')) {
        toRemove.push(child as Container)
      }
    }
    for (const child of toRemove) {
      reel.removeChild(child)
      child.destroy({ children: true })
    }
  }

  /** Clean up all display objects */
  destroy(): void {
    this.spriteTextures.forEach((t) => t.destroy(false))
    this.spriteTextures.clear()
    this.container.destroy({ children: true })
  }
}
